import React from 'react';
import {
  Loader2, AlertTriangle, ShieldAlert, DollarSign, Film, Users, Gauge,
  Clock, Pencil, Play, Sparkles, Image as ImageIcon,
} from 'lucide-react';

// Normalized pre-flight report AE returns on ANY dry-run (app/services/preflight.py), surfaced on the
// Studio thread message as `preflight`. EVERY field is optional — an older AE or a sparse edit-lane
// dry-run may send only a few. Render what's present, omit the rest, never crash.
export interface Preflight {
  lane?: string;
  model?: string;
  resolution?: string;
  duration_s?: number;
  asked_s?: number | string;
  wps?: number;
  word_count?: number;
  clips?: Array<{ idx?: number; seconds?: number; speakers?: string[]; spoken_text?: string }>;
  cast?: Array<{ name?: string; on_camera?: boolean }>;
  cost?: { credits?: number; usd?: number; rate_measured?: boolean };
  warnings?: Array<{ severity?: string; field?: string; message?: string }>;
  summary?: string;
  confidence?: { score?: number; band?: string; factors?: Array<{ name?: string; weight?: number; note?: string }> };
  [k: string]: any;
}

// The legacy dry_run_preview shape (pre-preflight AE). Used only as a graceful fallback so an older
// engine still shows a usable card (cost + the exact spoken lines) instead of a bare JSON dump.
interface LegacyPreview {
  projected_usd?: number;
  clips?: Array<{ index?: number; seconds?: number; spoken_line?: string; scene?: string; prompt?: string }>;
  [k: string]: any;
}

interface PreflightCardProps {
  preflight?: Preflight | null;
  /** Older AE only returns dry_run_preview — fall back to it for cost + clips when preflight is absent. */
  legacy?: LegacyPreview | null;
  /** The real run is being dispatched. */
  busy?: boolean;
  onEdit?: () => void;
  onRunReal?: () => void;
  /** Opt-in (~$0.04): draw ONE still of the planned first frame. Omitted → no button. Never automatic. */
  onPreviewImage?: () => void;
}

const isNum = (v: any): v is number => typeof v === 'number' && !Number.isNaN(v);

// A labelled stat tile — omits itself entirely when there is nothing to show.
function Stat({ icon, label, children }: { icon: React.ReactNode; label: string; children?: React.ReactNode }) {
  if (children == null || children === '') return null;
  return (
    <div className="rounded-xl border border-border bg-background/40 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500">{icon}{label}</div>
      <div className="mt-0.5 text-sm text-gray-900 dark:text-white">{children}</div>
    </div>
  );
}

const bandColor = (band?: string) =>
  band === 'High' ? 'text-emerald-500'
    : band === 'Low' ? 'text-red-500'
      : 'text-amber-500';

const bandDot = (band?: string) =>
  band === 'High' ? 'bg-emerald-500'
    : band === 'Low' ? 'bg-red-500'
      : 'bg-amber-500';

// blocks = impossible as asked (red); adjusts = the engine will change something (amber); anything
// else is informational (slate).
const sev = (s?: string) =>
  s === 'blocks'
    ? { ring: 'border-red-500/40 bg-red-500/10', text: 'text-red-300', tag: 'text-red-400', label: 'blocks' }
    : s === 'adjusts'
      ? { ring: 'border-amber-500/40 bg-amber-500/10', text: 'text-amber-200', tag: 'text-amber-400', label: 'adjusts' }
      : { ring: 'border-border bg-background/40', text: 'text-gray-300', tag: 'text-gray-400', label: s || 'note' };

/**
 * Pre-flight card for a Studio dry-run: a normalized, trust-first report of exactly what the real
 * run would produce (model, length, the words each cut speaks, cast, cost, watch-outs, confidence)
 * BEFORE anything spends. "Run for real" re-dispatches the SAME request without dry_run; a `blocks`
 * warning keeps the user on Edit instead of pretending the plan can run.
 */
export function PreflightCard({ preflight, legacy, busy, onEdit, onRunReal, onPreviewImage }: PreflightCardProps) {
  const pf: Preflight = preflight || {};
  const lg: LegacyPreview = legacy || {};

  // Cost — prefer the normalized preflight, fall back to the legacy projected_usd.
  const usd = isNum(pf.cost?.usd) ? pf.cost!.usd
    : isNum(lg.projected_usd) ? lg.projected_usd : undefined;
  const credits = isNum(pf.cost?.credits) ? pf.cost!.credits : undefined;
  const isEstimate = pf.cost ? pf.cost.rate_measured === false : true;

  // Clips — preflight speaks `spoken_text`; the legacy shape used `spoken_line`.
  const clips = (Array.isArray(pf.clips) && pf.clips.length)
    ? pf.clips.map((c, i) => ({ idx: isNum(c.idx) ? c.idx : i, seconds: c.seconds, speakers: c.speakers, text: c.spoken_text }))
    : (Array.isArray(lg.clips) ? lg.clips.map((c, i) => ({ idx: isNum(c.index) ? c.index : i, seconds: c.seconds, speakers: undefined as string[] | undefined, text: c.spoken_line || c.scene || c.prompt })) : []);

  // Per-clip cost share — proportional to each cut's seconds; if none carry seconds, split the total
  // evenly. Only computed when there's a real total and at least one clip (else the block renders nothing).
  const totalSec = clips.reduce((s, c) => s + (isNum(c.seconds) ? c.seconds : 0), 0);
  const clipCosts = (isNum(usd) && clips.length > 0)
    ? clips.map((c, i) => ({
        idx: isNum(c.idx) ? c.idx : i,
        seconds: isNum(c.seconds) ? c.seconds : undefined,
        share: totalSec > 0 ? usd * ((isNum(c.seconds) ? c.seconds : 0) / totalSec) : usd / clips.length,
      }))
    : [];

  const cast = Array.isArray(pf.cast) ? pf.cast : [];
  const onCam = cast.filter(c => c.on_camera).length;
  const offCam = cast.length - onCam;
  const castLabel = cast.length
    ? `${onCam} on-camera${offCam ? ` + ${offCam} off-camera` : ''}`
    : '';

  const warnings = Array.isArray(pf.warnings) ? pf.warnings : [];
  const hasBlock = warnings.some(w => w && w.severity === 'blocks');
  const blockMsgs = warnings.filter(w => w && w.severity === 'blocks').map(w => w.message).filter(Boolean);

  const conf = pf.confidence || null;
  const score = isNum(conf?.score) ? Math.max(0, Math.min(100, conf!.score!)) : undefined;
  const filledDots = isNum(score) ? Math.round(score / 20) : 0;

  // Show only what a user understands — model + resolution. `lane` is an internal routing name; not shown.
  const header = [pf.model, pf.resolution].filter(Boolean);
  const lengthLabel = isNum(pf.duration_s)
    ? `≈${pf.duration_s}s${pf.asked_s != null && pf.asked_s !== '' ? ` (asked: ${pf.asked_s})` : ''}`
    : '';
  const paceLabel = isNum(pf.wps) || isNum(pf.word_count)
    ? `${isNum(pf.wps) ? `${pf.wps} words/sec` : ''}${isNum(pf.wps) && isNum(pf.word_count) ? ' · ' : ''}${isNum(pf.word_count) ? `${pf.word_count} words` : ''}`
    : '';

  const runLabel = isNum(usd) ? `Generate now → $${Number(usd).toFixed(2)}` : 'Generate now';

  return (
    <div className="max-w-2xl w-full rounded-2xl border border-border bg-card overflow-hidden">
      {/* header — preview banner + model · resolution · lane */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
        <div className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-500">
          <Sparkles className="w-4 h-4" /> Preview — nothing has been spent yet
        </div>
        {header.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {header.map((h, i) => (
              <span key={i} className="inline-flex items-center rounded-full border border-border bg-background/40 px-2 py-0.5 text-[11px] text-gray-400">{h}</span>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 space-y-3 text-sm">
        {pf.summary && <p className="text-xs text-gray-500 leading-relaxed">{pf.summary}</p>}

        {/* impossible-as-asked banner — keeps the user on Edit, never framed as a failed render */}
        {hasBlock && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-sm font-medium text-red-400">
              <ShieldAlert className="w-4 h-4" /> Can't run as asked
            </div>
            {blockMsgs.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-xs text-red-300 list-disc list-inside">
                {blockMsgs.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            )}
            <p className="mt-1 text-[11px] text-red-300/80">Adjust the brief and preview again.</p>
          </div>
        )}

        {/* stat tiles — each omits itself when empty */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Stat icon={<Clock className="w-3 h-3" />} label="Length">{lengthLabel}</Stat>
          <Stat icon={<Film className="w-3 h-3" />} label="Cuts">{clips.length ? `${clips.length} clip${clips.length > 1 ? 's' : ''}` : ''}</Stat>
          <Stat icon={<Gauge className="w-3 h-3" />} label="Pace">{paceLabel}</Stat>
          <Stat icon={<Users className="w-3 h-3" />} label="Cast">{castLabel}</Stat>
          <Stat icon={<DollarSign className="w-3 h-3" />} label="Cost">
            {isNum(usd) ? (
              <span className="inline-flex items-center gap-1.5">
                ${Number(usd).toFixed(2)}{isNum(credits) ? <span className="text-gray-500">· {credits} cr</span> : null}
                {isEstimate && <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-medium text-amber-400">estimate</span>}
              </span>
            ) : ''}
          </Stat>
        </div>

        {/* per-clip cost breakdown — collapsible; sits under the Cost tile. Each cut's share is
            proportional to its seconds (else an even split), and the total re-states the Cost. */}
        {clipCosts.length > 0 && (
          <details className="rounded-xl border border-border bg-background/40 px-3 py-2 [&_summary::-webkit-details-marker]:hidden">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500">
                <DollarSign className="w-3 h-3" /> Cost breakdown
                {isEstimate && <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-medium text-amber-400 normal-case tracking-normal">estimate</span>}
              </span>
              <span className="text-xs text-gray-400">${Number(usd).toFixed(2)}</span>
            </summary>
            <div className="mt-2 space-y-1">
              {clipCosts.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] text-gray-400">
                  <span>Clip {c.idx + 1}{isNum(c.seconds) ? ` · ~${c.seconds}s` : ''}</span>
                  <span>~${c.share.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-border pt-1 text-[11px] font-medium text-gray-300">
                <span>Total</span>
                <span>${Number(usd).toFixed(2)}</span>
              </div>
            </div>
          </details>
        )}

        {/* per-clip list — the exact words each cut will speak (the key trust element) */}
        {clips.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">The cuts</div>
            <div className="space-y-1.5">
              {clips.map((c, i) => (
                <div key={i} className="rounded-xl border border-border bg-background/40 px-3 py-2">
                  <div className="flex items-center justify-between text-[11px] text-gray-500">
                    <span>Clip {isNum(c.idx) ? c.idx + 1 : i + 1}{Array.isArray(c.speakers) && c.speakers.length ? ` · ${c.speakers.join(', ')}` : ''}</span>
                    {isNum(c.seconds) && <span>{c.seconds}s</span>}
                  </div>
                  {c.text && <div className="mt-0.5 text-xs text-gray-900 dark:text-white">“{c.text}”</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* watch-outs (non-blocking warnings are shown here; blocks are already banner-ed above) */}
        {warnings.filter(w => w && w.severity !== 'blocks').length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Watch-outs</div>
            <div className="space-y-1.5">
              {warnings.filter(w => w && w.severity !== 'blocks').map((w, i) => {
                const s = sev(w.severity);
                return (
                  <div key={i} className={`rounded-xl border px-3 py-2 ${s.ring}`}>
                    <div className={`flex items-center gap-1.5 text-[11px] ${s.text}`}>
                      <AlertTriangle className="w-3 h-3 shrink-0" />
                      <span className="min-w-0">{w.message}</span>
                    </div>
                    <div className={`mt-0.5 text-[10px] ${s.tag}`}>{s.label}{w.field ? ` · ${w.field}` : ''}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* confidence — dots + band + the factors that drove it (never a bare number) */}
        {conf && (isNum(score) || conf.band || (Array.isArray(conf.factors) && conf.factors.length)) && (
          <div className="rounded-xl border border-border bg-background/40 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wide text-gray-500">Confidence</div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  {[0, 1, 2, 3, 4].map(i => (
                    <span key={i} className={`w-2 h-2 rounded-full ${i < filledDots ? bandDot(conf.band) : 'bg-border'}`} />
                  ))}
                </div>
                <span className={`text-xs font-medium ${bandColor(conf.band)}`}>
                  {conf.band || ''}{isNum(score) ? ` ${score}%` : ''}
                </span>
              </div>
            </div>
            {Array.isArray(conf.factors) && conf.factors.length > 0 && (
              <ul className="mt-2 space-y-1">
                {conf.factors.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11px] text-gray-400">
                    <span className="mt-1 w-1 h-1 rounded-full bg-gray-500 shrink-0" />
                    <span><span className="text-gray-300">{f.name}</span>{f.note ? ` — ${f.note}` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* actions — Edit returns to compose; Run for real re-dispatches WITHOUT dry_run (blocked plan can't run) */}
      <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
        <button
          onClick={onEdit}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-sm text-gray-300 hover:bg-accent disabled:opacity-50">
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
        {onPreviewImage && (
          <button
            onClick={onPreviewImage}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-1.5 text-sm text-gray-300 hover:bg-accent disabled:opacity-50">
            <ImageIcon className="w-3.5 h-3.5" /> Show me a preview image (~$0.04)
          </button>
        )}
        <button
          onClick={onRunReal}
          disabled={busy || hasBlock}
          title={hasBlock ? "Can't run as asked — adjust the brief first" : undefined}
          className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/25 hover:bg-primary/90 disabled:opacity-40 disabled:shadow-none">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-3.5 h-3.5 fill-current" />} {runLabel}
        </button>
      </div>
    </div>
  );
}
